import type { EvidenceClaim } from '@cadencia/domain';
import {
  evidenceFreshness,
  validateEvidenceSource,
  validateRetrieved,
  type CoachAIProvider,
  type Freshness,
} from '@cadencia/domain';
import { aiEvidenceReviewPayloadSchema, type EvidenceSource } from '@cadencia/contracts';
import { logger } from '../../lib/logger.js';
import { todayLocal } from '../../lib/dates.js';
import {
  listSearches,
  listSources,
  saveCandidate,
  saveSearch,
} from '../repositories/evidenceRepo.js';
import type { WriteContext } from '../writes.js';
import { motivoDeFalloIA } from './motivoIA.js';

/**
 * Revisar literatura y guardar lo que traiga (§8 del encargo).
 *
 * LA CADENA ENTERA, Y DÓNDE SE CORTA CADA COSA
 *
 *   1. Se pregunta al modelo, que busca con herramientas de servidor.
 *   2. La Edge Function devuelve las fichas Y las URL que de verdad visitó.
 *   3. `validateRetrieved` tira las fichas cuya procedencia no ocurrió.
 *   4. `validateEvidenceSource` tira las que están mal formadas o le piden a
 *      una fuente aguda que respalde un cambio de meses.
 *   5. Lo que sobrevive se guarda como **candidato**. Ninguna programación lo
 *      usa todavía.
 *
 * El paso 5 es la frontera del §8 entre «búsqueda y síntesis» y «evaluación y
 * activación», y se respeta aquí y no solo en la pantalla: si este servicio
 * pudiera activar fichas, la frontera sería una costumbre.
 *
 * LO QUE ESTE SERVICIO NO HACE, y es deliberado: **no bloquea nada**. Devuelve
 * lo que consiguió, y si no consiguió nada lo dice. El §8 exige que una
 * actualización fallida no impida entrenar.
 */

export interface RevisionResultado {
  /** Fichas nuevas guardadas como candidatas. */
  guardadas: number;
  /** Las que el dominio rechazó, con su motivo. Se enseña. */
  rechazadas: { title: string; reason: string }[];
  /** `null` si la IA no contestó: no es lo mismo que «no encontró nada». */
  searchId: string | null;
  /** El motivo cuando no hubo revisión. */
  motivo: string | null;
}

/** Estado de la base, para enseñarlo y para decidir si toca revisar. */
export async function estadoDeLaEvidencia(
  userId: string,
  hoy = todayLocal(),
): Promise<{ freshness: Freshness; sources: EvidenceSource[] }> {
  const [searches, sources] = await Promise.all([listSearches(userId), listSources(userId)]);
  return { freshness: evidenceFreshness(searches, hoy), sources };
}

/**
 * Lanza una revisión sobre una pregunta y guarda lo que traiga.
 *
 * `question` y `claims` los pone quien llama. NADA del atleta viaja: el §14
 * prohíbe mandar identidad o historial a buscadores bibliográficos, y el
 * contexto de este propósito no tiene dónde ponerlo.
 */
export async function revisarEvidencia(
  ctx: WriteContext,
  ia: CoachAIProvider,
  question: string,
  claims: readonly EvidenceClaim[],
  hoy = todayLocal(),
): Promise<RevisionResultado> {
  if (!ia.enabled) {
    return { guardadas: 0, rechazadas: [], searchId: null, motivo: 'la IA está desactivada' };
  }

  // Lo que ya hay, para no volver a fichar lo mismo. Solo identificadores: el
  // contenido ya lo tenemos y mandarlo sería pagar dos veces por él.
  const previas = await listSources(ctx.userId);
  const known = previas
    .flatMap((fila) => [fila.doi, fila.pmid])
    .filter((valor): valor is string => valor !== null)
    .slice(0, 60);

  const resultado = await ia.reviewEvidence({ question, claims, today: hoy, known });
  if (!resultado.ok) {
    return {
      guardadas: 0,
      rechazadas: [],
      searchId: null,
      // El motivo VIAJA CON EL RESULTADO y se pinta donde está el botón: una
      // búsqueda que se quedó sin tiempo y una sin red se arreglan de formas
      // distintas, y «unavailable» a secas no distingue ninguna de las dos.
      motivo: motivoDeFalloIA(resultado.error, 'revisar'),
    };
  }

  /*
   * El payload trae ADEMÁS `retrievedUrls`, que lo añadió el servidor. Se
   * vuelve a validar aquí con el esquema extendido: el esquema que la Edge
   * Function usó contra la API es el otro —sin ese campo— y entre los dos hay
   * una red que el cliente no controla.
   */
  const payload = aiEvidenceReviewPayloadSchema.safeParse(resultado.value);
  if (!payload.success) {
    logger.warn('ai', 'EVIDENCE_PAYLOAD_INVALID', 'La revisión no trae la forma esperada', {
      detalle: payload.error.message.slice(0, 200),
    });
    if (resultado.decisionId !== undefined) {
      ia.recordVerdict?.(resultado.decisionId, {
        accepted: false,
        reason: `forma inesperada: ${payload.error.message}`.slice(0, 300),
      });
    }
    return {
      guardadas: 0,
      rechazadas: [],
      searchId: null,
      motivo: 'la respuesta no traía la forma esperada',
    };
  }

  const revision = payload.data;

  // La búsqueda se guarda SIEMPRE, traiga fichas o no. «He buscado y no hay
  // nada aplicable» es un resultado, y sin registro es indistinguible de no
  // haber buscado.
  const busqueda = await saveSearch(ctx, {
    question,
    claims: [...claims],
    queries: revision.queries,
    searchedOn: hoy,
    coversFrom: revision.coversFrom,
    coversTo: revision.coversTo,
    selectionCriteria: revision.selectionCriteria,
    retrievedCount: revision.retrievedCount,
    keptCount: revision.keptCount,
    limitations: revision.limitations,
    provider: 'anthropic',
    // La que informa el servidor. Una función desplegada antes del 24/09/2026
    // no la manda, y entonces se dice que no se sabe en vez de suponerla.
    promptVersion: resultado.promptVersion ?? 'evidence_review.sin_informar',
  });

  const procedencia = validateRetrieved(revision.sources, revision.retrievedUrls);
  const rechazadas = [...procedencia.rejected];
  let guardadas = 0;

  for (const card of procedencia.accepted) {
    const candidata = {
      ...card,
      reviewedOn: hoy,
      searchId: busqueda.id,
      statusNote: null,
    };

    // La forma se comprueba ANTES de escribir. `validateEvidenceSource` necesita
    // la fila entera, así que se le da una con los campos que el repositorio
    // pondría: lo que se juzga es lo que se guardaría.
    const forma = validateEvidenceSource({
      ...candidata,
      id: '00000000-0000-4000-8000-000000000000',
      userId: ctx.userId,
      createdAt: '1970-01-01T00:00:00.000Z',
      updatedAt: '1970-01-01T00:00:00.000Z',
      deletedAt: null,
      rev: 1,
      originDeviceId: ctx.deviceId,
      syncSeq: null,
      status: 'candidate',
      version: 1,
    });

    if (!forma.ok) {
      rechazadas.push({ title: card.title, reason: forma.violations.join('; ') });
      continue;
    }

    await saveCandidate(ctx, candidata);
    guardadas += 1;
  }

  if (rechazadas.length > 0) {
    // Cuántas fichas se caen y por qué dice algo sobre el prompt que no se
    // puede saber de otra forma. No es un error: es la cadena funcionando.
    logger.warn('ai', 'EVIDENCE_CARDS_REJECTED', 'Fichas descartadas por el dominio', {
      rechazadas: rechazadas.length,
      motivos: rechazadas.map((f) => f.reason).slice(0, 5),
    });
  }

  /*
   * El veredicto FINAL (encargo del 23/09/2026, §3): la prosa no bastaba, y la
   * procedencia y la forma de cada ficha se juzgan aquí. Volver sin fichas es
   * una respuesta válida; traerlas y que no pase ninguna, no.
   */
  if (resultado.decisionId !== undefined) {
    const traidas = revision.sources.length;
    ia.recordVerdict?.(resultado.decisionId, {
      accepted: traidas === 0 || guardadas > 0,
      reason:
        rechazadas.length === 0
          ? null
          : `${String(rechazadas.length)} de ${String(traidas)} fichas descartadas: ${rechazadas
              .map((r) => r.reason)
              .join('; ')}`.slice(0, 300),
    });
  }

  return { guardadas, rechazadas, searchId: busqueda.id, motivo: null };
}
